import React, { memo, useState, useCallback, useRef, useLayoutEffect, useEffect, useMemo } from "react";
import { Position, useReactFlow, NodeResizeControl } from "reactflow";
import { useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";

import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

import OpenInFullRoundedIcon from "@mui/icons-material/OpenInFullRounded";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import GroupsIcon from "@mui/icons-material/Groups";
import TransformIcon from "@mui/icons-material/Transform";
import InfoIcon from "@mui/icons-material/Info";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FlipToBackIcon from "@mui/icons-material/FlipToBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import DoneIcon from "@mui/icons-material/Done";
import TipsAndUpdatesOutlinedIcon from "@mui/icons-material/TipsAndUpdatesOutlined";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import OutboundIcon from "@mui/icons-material/Outbound";

import { ConfirmPopup } from "../../../Common";
import OverflowTip from "../../../Common/OverflowTip";
import OverflowDiv from "../../../DataCatalog/OverflowDiv";
import { showToast } from "../../../../store/actions";
import CustomHandle from "./CustomHandle";
import { handleEditJob, handleJobRun, openConfirm, openStopConfirm } from "../../../../store/actions/datapipeline";
import { MESSAGES } from "../utils/JobData";
import { hexToRGBA, isColorDark, lightenColor } from "../../../../util/common";
import CloneJob from "../CloneJob";

const nodes = {
  1: { icon: <AccountTreeOutlinedIcon style={{ color: "#00ADB5" }} />, backgroundColor: "#00ADB5" },
  4: { icon: <CheckCircleIcon style={{ color: "#F67280" }} />, backgroundColor: "#F67280" },
  5: { icon: <EmojiEventsIcon style={{ color: "rgb(59, 255, 207)" }} />, backgroundColor: "rgb(59, 255, 207)" },
  6: { icon: <FlipToBackIcon style={{ color: "#02C105" }} />, backgroundColor: "#02C105" },
  2: {
    icon: <CompareArrowsIcon style={{ color: "#AD8B73" }} />,
    backgroundColor: "#AD8B73",
  },
  3: {
    icon: <TransformIcon style={{ color: "#BEAEE2" }} />,
    backgroundColor: "#BEAEE2",
  },
  7: { icon: <TipsAndUpdatesOutlinedIcon style={{ color: "#3C37C2" }} />, backgroundColor: "#3C37C2" },
  8: { icon: <AltRouteIcon style={{ color: "#808f85" }} />, backgroundColor: "#808f85" },
  9: { icon: <NotificationsActiveIcon style={{ color: "#E28E5C" }} />, backgroundColor: "#E28E5C" },
  groupWidget: {
    icon: <GroupsIcon style={{ color: "rgba(255, 0, 0, 0.2)" }} />,
    backgroundColor: "rgba(255, 0, 0, 0.2)",
  },
  widget: {
    sidebarIcon: <InfoIcon style={{ color: "#ffcd38" }} />,
    icon: <InfoIcon style={{ color: "#222222" }} />,
    backgroundColor: "#ffcd38",
  },
};

const jobTypeName = {
  1: "Data Load",
  2: "Data Exchange",
  3: "Data Transform",
  4: "Data Quality",
  5: "Data Mastering",
  6: "Data Deduplication",
  7: "Notebook",
  8: "Routing",
  9: "Notification",
};

const DATA_MODELING_JOBS = [1, 4, 5, 6];
const NON_MODELING_JOBS = [2, 3, 7, 8, 9];

const Arrow = memo(({ direction }) => {
  let style = {
      position: "absolute",
      overflow: "visible",
      zIndex: 1,
    },
    sx = {
      position: "absolute",
      fontSize: 20,
      color: "gray",
    };
  let tooltip = "";
  let svg = {};

  if (direction === "right") {
    sx = {
      ...sx,
      right: -25,
      top: "50%",
      transform: "translateY(-50%)",
    };
    tooltip = "Next job in the different pipeline";
    svg = (
      <svg
        style={{
          ...style,
          right: -7,
          top: "50%",
          transform: "translateY(-50%)",
        }}
        width="5"
        height="2"
      >
        <line x1="0" y1="1" x2="5" y2="1" stroke="gray" strokeWidth="1.5" />
      </svg>
    );
  } else if (direction === "bottom") {
    sx = {
      ...sx,
      bottom: -25,
      left: "50%",
      transform: "translateX(-50%)",
    };
    tooltip = "Error job in the different pipeline";
    svg = (
      <svg
        style={{
          ...style,
          bottom: -7,
          left: "50%",
          transform: "translateX(-50%)",
        }}
        width="2"
        height="5"
      >
        <line x1="1" y1="0" x2="1" y2="5" stroke="gray" strokeWidth="1.5" />
      </svg>
    );
  } else {
    sx = {
      ...sx,
      right: -20,
      bottom: -20,
    };
    tooltip = "Alternate job in the different pipeline";
    svg = (
      <svg
        style={{
          ...style,
          bottom: -5,
          right: -5,
        }}
        width="5"
        height="5"
      >
        <line x1="0" y1="0" x2="5" y2="5" stroke="gray" strokeWidth="1.5" />
      </svg>
    );
  }

  return (
    <>
      {svg}
      <OutboundIcon sx={sx} titleAccess={tooltip} />
    </>
  );
});

export const Node = memo((node) => {
  const { Workspace_id, DataPipeline_id } = useParams();

  const dataPipeline = useSelector((state) => state.dataPipeline);
  const { editJob } = useMemo(() => dataPipeline, [dataPipeline]);

  const dispatch = useDispatch();
  const { id, data } = node;
  const { type, label, status, savePipeline = () => {}, updateNodes = () => {} } = data;
  const { icon, backgroundColor } = nodes[data.type] || nodes[1];

  const reactFlowInstance = useReactFlow();
  const edges = reactFlowInstance.getEdges();

  const { hasNextJob, isDifferentPipeline: differentNextPipeline } = useMemo(() => {
    const edge = edges.find(({ source, sourceHandle }) => sourceHandle === "right" && source === id);
    return { hasNextJob: !!edge, isDifferentPipeline: !!edge?.isDifferentPipeline };
  }, [edges, id]);

  const { hasErrorJob, isDifferentPipeline: differentErrorPipeline } = useMemo(() => {
    const edge = edges.find(({ source, sourceHandle }) => sourceHandle === "bottom" && source === id);
    return { hasErrorJob: !!edge, isDifferentPipeline: !!edge?.isDifferentPipeline };
  }, [edges, id]);

  const { hasAlternativeJob, isDifferentPipeline: differentAlternatePipeline } = useMemo(() => {
    const edge = edges.find(({ source, sourceHandle }) => sourceHandle === "right_bottom" && source === id);
    return { hasAlternativeJob: !!edge, isDifferentPipeline: !!edge?.isDifferentPipeline };
  }, [edges, id]);

  const [loading, setLoading] = useState(false);
  const [deleteNode, setDeleteNode] = useState(null);
  const [cloneNode, setCloneNode] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = useCallback((event) => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleEditClick = useCallback(
    (node) => {
      if (editJob === null) dispatch(handleEditJob(node));
      else data.editPanelRef.current.handleCloseOrReplacePanel({}, node);
      handleClose();
    },
    [editJob, data.editPanelRef, dispatch, handleClose]
  );

  const handleDeleteClick = useCallback(
    (node) => {
      if (editJob !== null) dispatch(showToast({ message: MESSAGES.DataPipeline.EditPanelOpen, type: "error" }));
      else setDeleteNode(node);
    },
    [dispatch, editJob]
  );

  const handleCloneClick = useCallback(
    (node) => {
      if (editJob !== null) dispatch(showToast({ message: MESSAGES.DataPipeline.EditPanelOpen, type: "error" }));
      else setCloneNode(node);
    },
    [dispatch, editJob]
  );

  const handleContextMenu = useCallback(
    (event) => {
      event.preventDefault();
      handleClick(event);
    },
    [handleClick]
  );

  //Deleting node form array
  //Later this will be deleted from db when user click on save
  const onDeleteNode = useCallback(() => {
    reactFlowInstance.deleteElements({ nodes: [{ id }] });
    setDeleteNode(null);
  }, [id, reactFlowInstance]);

  const ungroupNode = useCallback(() => {
    if (editJob !== null) return dispatch(showToast({ message: MESSAGES.DataPipeline.EditPanelOpen, type: "error" }));
    reactFlowInstance.setNodes((prev) =>
      prev.map((node) => {
        if (node.id === id) {
          const parentNode = prev.find((_node) => _node.id === node.parentNode);

          if (parentNode) {
            delete node.parentNode;
            delete node.extent;
            node.position = {
              x: parentNode.position.x + node.position.x,
              y: parentNode.position.y + node.position.y,
            };
          }
        }

        return node;
      })
    );

    setAnchorEl(null);
  }, [dispatch, editJob, id, reactFlowInstance]);

  const handleRun = useCallback(async () => {
    let allowedToRun = !["running", "draft"].includes(status);
    if (DATA_MODELING_JOBS.includes(type) && allowedToRun) dispatch(openConfirm({ Job_id: id, JobType: type }));
    else if (NON_MODELING_JOBS.includes(type) && allowedToRun) {
      setLoading(true);
      const response = await dispatch(handleJobRun(Workspace_id, id, type));

      if (response?.success) {
        const { Job_id, status } = response.data;

        reactFlowInstance.setNodes((prev) => {
          return prev.map((node) => (node.id === Job_id ? { ...node, data: { ...node.data, status } } : node));
        });

        dispatch(
          showToast({
            message: response.message,
            type: "success",
          })
        );
      }

      setLoading(false);
    }
  }, [Workspace_id, dispatch, id, reactFlowInstance, status, type]);

  const handleStop = useCallback(async () => {
    if (editJob !== null) return dispatch(showToast({ message: MESSAGES.DataPipeline.EditPanelOpen, type: "error" }));

    dispatch(
      openStopConfirm({
        Job_id: id,
        JobName: label,
        JobType: type,
      })
    );
  }, [dispatch, editJob, id, label, type]);

  //Removing Edge
  const unLink = useCallback(
    (_sourceHandle) => {
      if (editJob !== null) dispatch(showToast({ message: MESSAGES.DataPipeline.EditPanelOpen, type: "error" }));
      else {
        reactFlowInstance.setEdges((prev) =>
          prev.filter(({ sourceHandle, source }) => !(sourceHandle === _sourceHandle && source === id))
        );

        //Updating current node with itself just to fix UI render issue
        reactFlowInstance.setNodes((prev) =>
          prev.map((node) => (node.id === id ? { ...node, data: { ...node.data } } : node))
        );

        setAnchorEl(null);
      }
    },
    [dispatch, editJob, id, reactFlowInstance]
  );

  return (
    <>
      <CustomHandle type="target" id="left" position={Position.Left} />
      <CustomHandle title="Go here if True" type="source" id="right" position={Position.Right} maxConnections={1} />
      {type === 8 && (
        <CustomHandle
          title="Go here if False"
          type="source"
          id="right_bottom"
          style={{ top: 28, right: -2 }}
          position={Position.Right}
          maxConnections={1}
        />
      )}
      <CustomHandle title="Go here if Error" type="source" id="bottom" position={Position.Bottom} maxConnections={1} />

      <div
        className={`dndnode`}
        style={{
          border: `2px solid ${backgroundColor}`,
          ...(editJob?.id === id ? { outline: `3px ${backgroundColor} dashed` } : {}),
          ...(["running", "terminating"].includes(status)
            ? { outline: `3px ${backgroundColor} dashed`, animation: "blink 1s steps(5, start) infinite" }
            : {}),
        }}
        draggable
      >
        {editJob?.id !== id && (
          <IconButton className="MoreVertIcon" style={{ background: backgroundColor }} onClick={handleContextMenu}>
            <MoreVertIcon style={{ fontSize: "14px", color: "#fff" }} />
          </IconButton>
        )}
        {icon}
        <span className="dndnode-text">{label}</span>
      </div>
      {differentNextPipeline && <Arrow direction="right" />}
      {differentErrorPipeline && <Arrow direction="bottom" />}
      {differentAlternatePipeline && <Arrow direction="right-bottom" />}
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={() => handleEditClick(node)}>Edit</MenuItem>
        {status !== "draft" &&
          (status === "running" || status === "terminating" ? (
            <MenuItem
              disabled={loading || status === "terminating"}
              onClick={status === "running" ? handleStop : handleRun}
            >
              Stop Job
            </MenuItem>
          ) : (
            <MenuItem
              disabled={loading || status === "running"}
              onClick={status === "running" ? handleStop : handleRun}
            >
              Run Job
            </MenuItem>
          ))}
        {!!reactFlowInstance.getNode(id)?.parentNode && <MenuItem onClick={ungroupNode}>Ungroup</MenuItem>}
        {hasNextJob && <MenuItem onClick={() => unLink("right")}>Unlink Success Job</MenuItem>}
        {hasErrorJob && <MenuItem onClick={() => unLink("bottom")}>Unlink Error Job</MenuItem>}
        {hasAlternativeJob && <MenuItem onClick={() => unLink("right_bottom")}>Unlink Alternative Job</MenuItem>}
        <MenuItem onClick={() => handleCloneClick(node)}>Clone Job</MenuItem>
        <MenuItem onClick={() => handleDeleteClick(node)}>Delete Job</MenuItem>
      </Menu>

      {cloneNode && (
        <CloneJob
          setOpenClone={() => {
            setCloneNode(null);
            setAnchorEl(null);
          }}
          pipelineId={parseInt(DataPipeline_id)}
          handleSave={(e, clone) => savePipeline(clone)}
          cloneNode={cloneNode}
          updateNodes={updateNodes}
        />
      )}

      {deleteNode && (
        <ConfirmPopup
          configData={{
            popupTitle: `Delete ${jobTypeName[deleteNode.data?.type] || ""} Job`,
            popupMessage: `Are you sure you want to delete "${deleteNode.data?.label}" job?`,
            handleCancel: () => setDeleteNode(null),
            handleClick: () => onDeleteNode(deleteNode.id),
            loadingForConfirm: loading,
          }}
        />
      )}
    </>
  );
});

export const SidebarNode = memo(({ data }) => {
  const { sidebarIcon, icon, backgroundColor } = nodes[data.type] || nodes[1];

  const dataPipeline = useSelector((state) => state.dataPipeline);

  const { editJob } = useMemo(() => dataPipeline, [dataPipeline]);
  const { Name } = data;

  const onDragStart = useCallback(
    (event) => {
      if (editJob != null) return;
      else {
        event.dataTransfer.setData("application/reactflow", data.type);
        event.dataTransfer.effectAllowed = "move";
      }
    },
    [data.type, editJob]
  );

  return (
    <div
      className="dndnode sidebar"
      style={{ marginBottom: "10px", border: `2px solid ${backgroundColor}` }}
      onDragStart={(event) => onDragStart(event)}
      draggable
    >
      {sidebarIcon || icon}
      <span className="dndnode-text">{Name}</span>
    </div>
  );
});

export const NodeWidget = (props) => {
  const { id, data } = props;

  const dataPipeline = useSelector((state) => state.dataPipeline);
  const { editJob } = useMemo(() => dataPipeline, [dataPipeline]);

  const { icon } = nodes[data.type];
  const { label } = data;
  const [editable, setEditable] = useState(false);
  const [nodeText, setNodeText] = useState(label);
  const [anchorEl, setAnchorEl] = useState(null);
  const [deleteNode, setDeleteNode] = useState(null);

  const dispatch = useDispatch();

  const handleClick = useCallback((event) => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  }, []);

  const handleContextMenu = useCallback(
    (event) => {
      event.preventDefault();
      handleClick(event);
    },
    [handleClick]
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const reactFlowInstance = useReactFlow();

  const handleOnBlur = useCallback(() => {
    setEditable(false);
    reactFlowInstance.setNodes((prev) =>
      prev.map((_node) =>
        _node.id === id
          ? {
              ..._node,
              data: {
                ..._node.data,
                label: nodeText,
              },
            }
          : _node
      )
    );
  }, [id, nodeText, reactFlowInstance]);

  const handleEditClick = useCallback(() => {
    if (editJob != null) return dispatch(showToast({ message: MESSAGES.DataPipeline.EditPanelOpen, type: "error" }));
    else setEditable(true);
    handleClose();
  }, [dispatch, editJob, handleClose]);

  const handleDeleteClick = useCallback(() => {
    if (editJob != null) return dispatch(showToast({ message: MESSAGES.DataPipeline.EditPanelOpen, type: "error" }));
    else setDeleteNode(props);
    handleClose();
  }, [dispatch, editJob, handleClose, props]);

  //Deleting node form array
  //Later this will be deleted from db when user click on save
  const onDeleteNode = useCallback(
    (nodeId) => {
      reactFlowInstance.deleteElements({ nodes: [{ id: nodeId }] });
      setDeleteNode(null);
    },
    [reactFlowInstance]
  );

  return (
    <>
      <div
        className={`dndnode notched`}
        style={{ marginBottom: "10px", backgroundColor: "amber" }}
        draggable
        onDoubleClick={handleEditClick}
      >
        <IconButton
          className="MoreVertIcon"
          style={{ backgroundColor: "#FFCD38", border: `1px solid black` }}
          onClick={handleContextMenu}
        >
          <MoreVertIcon style={{ fontSize: "14px" }} />
        </IconButton>
        {icon}
        {editable ? (
          <>
            <TextField
              variant="standard"
              value={nodeText}
              size="small"
              onChange={(e) => setNodeText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") handleOnBlur();
              }}
              onBlur={handleOnBlur}
              multiline
            />
            <IconButton size="small" onClick={handleOnBlur}>
              <DoneIcon />
            </IconButton>
          </>
        ) : (
          <>
            <span className="dndnode-text">{label}</span>
            <IconButton size="small" onClick={handleEditClick}>
              <EditIcon />
            </IconButton>
          </>
        )}
      </div>
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleEditClick}>Edit</MenuItem>
        <MenuItem onClick={handleDeleteClick}>Delete</MenuItem>
      </Menu>

      {deleteNode && (
        <ConfirmPopup
          configData={{
            popupTitle: "Delete Note",
            popupMessage: `Are you sure you want to delete this Note?`,
            handleCancel: () => setDeleteNode(null),
            handleClick: () => onDeleteNode(deleteNode.id),
            loadingForConfirm: false,
          }}
        />
      )}
    </>
  );
};

const controlStyle = {
  background: "transparent",
  border: "none",
};

const DEFAULT_GROUP_COLOR = "#ffc9c9";

export const GroupWidget = (props) => {
  const { id, data } = props;

  const { label, color: defaultColor = DEFAULT_GROUP_COLOR } = data;
  const [editable, setEditable] = useState(false);
  const [color, setColor] = useState(defaultColor);
  const [nodeText, setNodeText] = useState(label);
  const [anchorEl, setAnchorEl] = useState(null);
  const [deleteNode, setDeleteNode] = useState(null);
  const inputRef = useRef(null);
  const [size, setSize] = useState({
    height: 300,
    width: 400,
  });
  const dispatch = useDispatch();
  const dataPipeline = useSelector((state) => state.dataPipeline);
  const { editJob } = useMemo(() => dataPipeline, [dataPipeline]);

  const contexRef = useRef(null);

  const textColor = useMemo(() => (isColorDark(color) ? "white" : "black"), [color]);

  const handleClick = useCallback((event) => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  }, []);

  const handleContextMenu = useCallback(
    (event) => {
      event.preventDefault();
      handleClick(event);
    },
    [handleClick]
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleDeleteClick = useCallback(() => {
    if (editJob != null) return dispatch(showToast({ message: MESSAGES.DataPipeline.EditPanelOpen, type: "error" }));
    else setDeleteNode(props);
    handleClose();
  }, [dispatch, editJob, handleClose, props]);

  const reactFlowInstance = useReactFlow();

  const handleOnBlur = useCallback(() => {
    setEditable(false);
    reactFlowInstance.setNodes((prev) =>
      prev.map((_node) =>
        _node.id === id
          ? {
              ..._node,
              data: {
                ..._node.data,
                label: nodeText,
              },
            }
          : _node
      )
    );
  }, [id, nodeText, reactFlowInstance]);

  //Deleting node form array
  //Later this will be deleted from db when user click on save
  const onDeleteNode = useCallback(
    (node) => {
      const xPos = node.xPos || node?.position?.x;
      const yPos = node.yPos || node?.position?.x;

      //When deleting Group node just deleting groupWidget and keeping other nodes
      //Removing Group node and removing parentNode attribute from node
      reactFlowInstance.setNodes((prev) => {
        return prev
          .filter((_node) => _node.id !== node.id)
          .map((_node) => {
            if (_node.parentNode !== node.id) return _node;

            delete _node.parentNode;
            delete _node.extent;
            return {
              ..._node,
              position: {
                x: xPos + _node.position.x,
                y: yPos + _node.position.y,
              },
            };
          });
      });

      setDeleteNode(null);
    },
    [reactFlowInstance]
  );

  //Getting initial height and width
  useEffect(() => {
    if (!id || !reactFlowInstance) return;

    const currentNode = reactFlowInstance.getNodes().find(({ id: _id }) => _id === id);

    setSize({
      height: currentNode.height,
      width: currentNode.width,
    });
  }, [id, reactFlowInstance]);

  useLayoutEffect(() => {
    if (editable) inputRef.current.focus();
  }, [editable]);

  //Handle resize to not go inside child
  const shouldResize = useCallback(
    (event, params) => {
      const { direction, height, width, x, y } = params;

      if (direction[0] > 0 && direction[1] > 0) {
        setSize({ height, width });
        return true;
      }

      //Finding child nodes
      const childNodes = reactFlowInstance.getNodes().filter(({ parentNode }) => parentNode === id);
      if (childNodes.length === 0) {
        setSize({ height, width });
        return true;
      }

      let absoluteMaxX = Number.MIN_SAFE_INTEGER;
      let absoluteMaxY = Number.MIN_SAFE_INTEGER;

      //Get max available height/width
      childNodes.forEach(({ positionAbsolute, height, width }) => {
        if (positionAbsolute.x + width > absoluteMaxX) absoluteMaxX = positionAbsolute.x + width;
        if (positionAbsolute.y + height > absoluteMaxY) absoluteMaxY = positionAbsolute.y + height;
      });

      //Should resize or not boolean
      if (absoluteMaxX < x + width && absoluteMaxY < y + height) {
        setSize({ height, width });
        return true;
      }
      return false;
    },
    [id, reactFlowInstance]
  );

  const onResizeStart = useCallback(() => {
    if (editJob != null) return dispatch(showToast({ message: MESSAGES.DataPipeline.EditPanelOpen, type: "error" }));
  }, [dispatch, editJob]);

  const handleEditClick = useCallback(() => {
    if (editJob != null) return dispatch(showToast({ message: MESSAGES.DataPipeline.EditPanelOpen, type: "error" }));
    setEditable(true);
    handleClose(null);
  }, [dispatch, editJob, handleClose]);

  const handleSetColor = useCallback(
    (e) => {
      setColor(e.target.value);

      reactFlowInstance.setNodes((prev) =>
        prev.map((_node) =>
          _node.id === id
            ? {
                ..._node,
                data: {
                  ..._node.data,
                  color: e.target.value,
                },
              }
            : _node
        )
      );

      //To keep menu open
      setTimeout(() => {
        setAnchorEl(contexRef.current);
      }, 100);
    },
    [id, reactFlowInstance]
  );

  const handleResetClick = useCallback(() => {
    reactFlowInstance.setNodes((prev) =>
      prev.map((_node) =>
        _node.id === id
          ? {
              ..._node,
              data: {
                ..._node.data,
                color: DEFAULT_GROUP_COLOR,
              },
            }
          : _node
      )
    );

    setColor(DEFAULT_GROUP_COLOR);
  }, [id, reactFlowInstance]);

  return (
    <>
      <div
        className={`dndnode groupWidget`}
        draggable
        style={{
          height: `${size.height}px`,
          width: `${size.width}px`,
          backgroundColor: hexToRGBA(color, 0.6),
          color: textColor,
        }}
        onDoubleClick={handleEditClick}
      >
        <IconButton
          ref={contexRef}
          className="MoreVertIcon"
          style={{ color: textColor, backgroundColor: lightenColor(color, 0.6), border: `1px solid ${textColor}` }}
          onClick={handleContextMenu}
        >
          <MoreVertIcon style={{ fontSize: "14px" }} />
        </IconButton>
        <NodeResizeControl
          style={controlStyle}
          minWidth={200}
          minHeight={40}
          shouldResize={shouldResize}
          onResizeStart={onResizeStart}
        >
          <OpenInFullRoundedIcon
            fontSize="small"
            style={{
              position: "absolute",
              right: 5,
              bottom: 5,
              transform: "rotate(90deg)",
              color: textColor,
            }}
          />
        </NodeResizeControl>
        <Box style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          {editable ? (
            <>
              <TextField
                ref={inputRef}
                variant="standard"
                value={nodeText}
                size="small"
                onChange={(e) => setNodeText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") handleOnBlur();
                }}
                onBlur={handleOnBlur}
                inputProps={{
                  maxLength: 30,
                  style: {
                    color: textColor,
                  },
                }}
              />
              <IconButton
                size="small"
                style={{ color: textColor, backgroundColor: lightenColor(color, 0.6) }}
                onClick={handleOnBlur}
              >
                <DoneIcon />
              </IconButton>
            </>
          ) : (
            <>
              <OverflowTip
                title=<Typography variant="body2" sx={{ color: textColor }}>
                  {label ?? ""}
                </Typography>
                arrow
                placement="top"
              >
                <OverflowDiv sx={{ color: textColor }} cellContent={label ?? ""} width="100%" align="center" />
              </OverflowTip>
              <IconButton
                size="small"
                style={{ color: textColor, backgroundColor: lightenColor(color, 0.6) }}
                onClick={handleEditClick}
              >
                <EditIcon />
              </IconButton>
            </>
          )}
        </Box>
      </div>
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleEditClick}>Edit</MenuItem>
        <MenuItem onClick={() => handleDeleteClick(props)}>Delete</MenuItem>
        <MenuItem>
          <input type="color" defaultValue={color} onBlur={handleSetColor} />
          <Tooltip title="Reset color" sx={{ ml: 1 }}>
            <IconButton onClick={handleResetClick}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </MenuItem>
      </Menu>

      {deleteNode && (
        <ConfirmPopup
          configData={{
            popupTitle: "Delete Group",
            popupMessage: `Are you sure you want to delete this Group?`,
            handleCancel: () => setDeleteNode(null),
            handleClick: () => onDeleteNode(deleteNode),
            loadingForConfirm: false,
          }}
        />
      )}
    </>
  );
};
